import React, { useState, useEffect } from 'react';
import {
    Card,
    Alert,
    Badge
} from 'react-bootstrap';
import { BASE_URL } from '../../Constants/constants';
import { useAuth } from '../AuthContext/AuthContext';
import { getAuthenticatedAxios } from './axiosConfig';
import { formatDate, formatDateTimeFull, formatDateOnly } from './Dateutils';
import { useTranslation } from 'react-i18next';

const SecurityCheckoutStage = ({ checkoutStatus, onStatusChange }) => {
    const { t } = useTranslation('securityCheckoutStage');
    const { currentUser } = useAuth();
    const [error, setError] = useState(null);
    const [rejectionDetails, setRejectionDetails] = useState(null);

    // Fetch rejection history when security_checkout_status is null
    // (reset after rejection so staff can be reprocessed)
    useEffect(() => {
        if (currentUser?.id &&
            (checkoutStatus?.security_checkout_status === false ||
                checkoutStatus?.security_checkout_status === null) &&
            checkoutStatus?.ict_checkout_status === true) {
            fetchRejectionHistory();
        } else {
            setRejectionDetails(null);
        }
    }, [currentUser, checkoutStatus]);

    const fetchRejectionHistory = async () => {
        try {
            const authenticatedAxios = getAuthenticatedAxios();
            const response = await authenticatedAxios.get(
                `${BASE_URL}/checkout/rejections/${currentUser.id}?department=security`
            );
            if (response.data && response.data.length > 0) {
                const rejection = response.data[0];
                // Only show if rejection postdates last reset
                const lastReset = checkoutStatus?.last_reset_date
                    ? new Date(checkoutStatus.last_reset_date)
                    : null;
                const rejectionDate = new Date(rejection.created_at);
                if (!lastReset || rejectionDate > lastReset) {
                    setRejectionDetails(rejection);
                } else {
                    setRejectionDetails(null);
                }
            }
        } catch (err) {
            console.error('Error fetching security rejection history:', err);
        }
    };

    const renderRejectionMessage = () => {
        if (!rejectionDetails) return null;
        return (
            <div className="bg-danger text-white p-4 rounded mb-3">
                <h4><i className="bi bi-shield-x me-2"></i>Security Checkout Rejected</h4>
                <div className="mb-2">
                    <strong>Reason:</strong> {rejectionDetails.reason}
                </div>
                <div className="mb-3">
                    <strong>Rejection Date:</strong> {new Date(rejectionDetails.created_at).toLocaleString()}
                </div>
                <div>
                    Please review the reason above, resolve the issue with the Security department,
                    and await re-processing. Security will need to process your checkout again.
                </div>
            </div>
        );
    };

    const renderContent = () => {
        // If checkout is complete, show completion message
        if (checkoutStatus?.is_checkout_complete) {
            return (
                <Alert variant="success">
                    <Alert.Heading>
                        <i className="bi bi-check-circle-fill me-2"></i>
                        {t('completion.title')}
                    </Alert.Heading>
                    <p>{t('completion.message')}</p>
                    <hr />
                    <p className="mb-0">
                        <strong>{t('completion.completion_date')}</strong> {formatDate(checkoutStatus.checkout_completion_date)}
                    </p>
                </Alert>
            );
        }

        // If security checkout is already completed
        if (checkoutStatus?.security_checkout_status === true) {
            return (
                <Alert variant="success">
                    <Alert.Heading>
                        <i className="bi bi-shield-check me-2"></i>
                        {t('security_completed.title')}
                    </Alert.Heading>
                    <p>{t('security_completed.message')}</p>
                    <hr />
                    <div className="d-flex justify-content-between align-items-center">
                        <p className="mb-0">
                            <strong>{t('completion.completion_date')}</strong> {formatDate(checkoutStatus.security_checkout_date)}
                        </p>
                        {checkoutStatus.security_checked_by && (
                            <Badge bg="info">{t('security_completed.processed_by')} {checkoutStatus.security_checked_by}</Badge>
                        )}
                    </div>
                    {checkoutStatus.security_checkout_notes && (
                        <div className="mt-2">
                            <small className="text-muted">
                                <strong>{t('security_completed.notes_label')}</strong> {checkoutStatus.security_checkout_notes}
                            </small>
                        </div>
                    )}
                </Alert>
            );
        }

        // Render rejection message if any
        const rejectionMessage = renderRejectionMessage();
        if (rejectionMessage) return rejectionMessage;

        return (
            <Alert variant="info">
                <Alert.Heading>
                    <i className="bi bi-shield-exclamation me-2"></i>
                    {t('in_progress.title')}
                </Alert.Heading>
                <p>{t('in_progress.message')}</p>
                <ul>
                    <li>{t('in_progress.items.badge')}</li>
                    <li>{t('in_progress.items.radios')}</li>
                    <li>{t('in_progress.items.keys')}</li>
                    <li>{t('in_progress.items.access_cards')}</li>
                    <li>{t('in_progress.items.parking')}</li>
                    <li>{t('in_progress.items.clearance')}</li>
                    <li>{t('in_progress.items.documentation')}</li>
                </ul>
                <div className="mt-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span><strong>{t('in_progress.waiting')}</strong></span>
                    </div>
                    <p className="mb-0 mt-2">
                        <small className="text-muted">
                            {t('in_progress.contact_message')}
                        </small>
                    </p>
                </div>
            </Alert>
        );
    };

    return (
        <Card>
            <Card.Header>
                <h3>
                    <i className="bi bi-shield me-2"></i>
                    {t('heading')}
                </h3>
            </Card.Header>
            <Card.Body>
                {error && (
                    <Alert
                        variant="danger"
                        onClose={() => setError(null)}
                        dismissible
                    >
                        {error}
                    </Alert>
                )}

                {renderContent()}

                {checkoutStatus?.ict_checkout_date && (
                    <div className="mt-3">
                        <small className="text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            {t('ict_completion.label')} {formatDate(checkoutStatus.ict_checkout_date)}
                        </small>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default SecurityCheckoutStage;